import { type FocusEventHandler, useCallback, useRef } from 'react';

type UseFocusProps<T> = {
  onFocus?: FocusEventHandler<T>;
  onBlur?: FocusEventHandler<T>;
};

export function useFocus<T extends HTMLElement>({
  onBlur,
  onFocus,
}: UseFocusProps<T>) {
  const containerRef = useRef<T>(null);
  const isFocused = useRef(false);

  const handleBlur: FocusEventHandler<T> = useCallback(
    (event) => {
      const clickedOnNonFocusableElement = !event.relatedTarget;
      if (clickedOnNonFocusableElement) {
        isFocused.current = false;
        onBlur?.(event);
        return;
      }

      const element = containerRef.current;
      if (!element) {
        isFocused.current = false;
        onBlur?.(event);
        return;
      }

      const clickedOutside = !element.contains(event.relatedTarget);
      if (clickedOutside) {
        isFocused.current = false;
        onBlur?.(event);
        return;
      }
    },
    [onBlur],
  );

  const handleFocus: FocusEventHandler<T> = useCallback(
    (event) => {
      if (isFocused.current) {
        return;
      }

      isFocused.current = true;
      onFocus?.(event);
    },
    [onFocus],
  );

  return { handleFocus, handleBlur, containerRef };
}
